import { Info, Layers, Search, Send, Target, Undo2, UserCheck } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Textarea,
  toast,
} from '@/components/cargofl-ui';
import { Typewriter } from '@/components/typewriter';
import { ConfirmButton, ConfirmDialog } from '@/components/confirm-dialog';
import { useKraPermissions } from '@/components/kra/permissions';
import { ServerExportButton } from '@/components/server-export-button';
import { PageContainer } from '@/components/shell/page-container';
import { StatTile } from '@/components/stat-tile';
import { assessmentStatusVariant, goalAssignmentVariant, PILL_CLASS } from '@/lib/badges';
import {
  useCurrentCycle,
  useGoalOverview,
  usePublishMany,
  type GoalSetSummary,
} from '@/lib/goals-api';
import { useAssignKraSet, useKraSets, useUnassignKraSet } from '@/lib/kra-sets-api';

const ALL = '__all__';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

const STATUS_LABELS: Record<GoalSetSummary['status'], string> = {
  NOT_STARTED: 'Not started',
  DRAFT: 'Draft',
  // Both live. PENDING_ACCEPTANCE only survives on sets that were mid-acceptance when
  // the step was removed; nothing new reaches it.
  PENDING_ACCEPTANCE: 'Live',
  ACCEPTED: 'Live',
};

/**
 * Third person, and short: this is a table about other people, so never the shared
 * labels' "Awaiting your acceptance". Anything past In progress has been submitted.
 */
const ASSESSMENT_LABELS: Record<GoalSetSummary['assessmentStatus'], string> = {
  NOT_STARTED: 'Not started',
  SELF_IN_PROGRESS: 'In progress',
  AWAITING_MANAGER: 'Submitted',
  AWAITING_EMPLOYEE_ACCEPTANCE: 'Employee to accept',
  AWAITING_HR: 'At HR review',
  AWAITING_MANAGEMENT: 'At management',
  COMPLETED: 'Completed',
};

/**
 * Why this row cannot be published in a batch, or null when it can.
 *
 * DRAFT only. Re-publishing a live set is a real action, but it re-notifies the employee
 * and clears their outstanding change request — as a side effect of Select all that is a
 * mail to the whole company about goals that did not move. The server refuses it too;
 * this is so the row cannot be ticked for it in the first place.
 */
function publishBlocker(row: GoalSetSummary): string | null {
  if (row.status === 'NOT_STARTED' || row.assignmentId === null) {
    return 'No goal set yet. Assign a KRA group, or open it and write the goals.';
  }
  if (row.status !== 'DRAFT') {
    return 'Already live. Publish a correction from this employee’s own goal set, so they are told what changed.';
  }
  return null;
}

/**
 * Goal assignments.
 *
 * One screen for the whole setup half of the cycle: who has goals, who does not, giving
 * them some, and making them live. It used to be two — this list, read-only with an Open
 * link, and an "Assign KRA" tab with checkboxes over the same people with the same
 * columns. Nobody could see from either one whether they were looking at everybody, and
 * they were not: this list was org-wide and that one was scoped to the reporting line.
 *
 * Not HR-only, deliberately. Assigning goals to your own reports is a manager's job, and
 * behind an /hr/ route it was not theirs to do. The server scopes the rows — HR and
 * Department Heads read everybody, a manager reads their own reports — and decides
 * `canAssign` per row, so this screen shows one person everything they may act on and
 * nothing they may not.
 *
 * The two actions have different preconditions, and that is the point of one checkbox
 * with two counted buttons: a row with no goals can be assigned and not published, a
 * draft with goals can be published and not assigned. Each button says how many of the
 * ticked rows it will actually touch, so the number is never a surprise.
 */
export function GoalAssignmentsPage() {
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<string>(ALL);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  /** The group picked from the dropdown, held until it is confirmed. */
  const [pendingGroup, setPendingGroup] = React.useState<{ id: string; name: string } | null>(
    null,
  );
  const [unassigning, setUnassigning] = React.useState(false);
  const [unassignReason, setUnassignReason] = React.useState("");

  // The same rule the library page and the API gate on, so the link never leads
  // somewhere that turns you away.
  const { canManage: canManageKra } = useKraPermissions();
  const { data, isPending, error } = useGoalOverview();
  const { data: cycle } = useCurrentCycle();
  const { data: sets } = useKraSets();
  const publishMany = usePublishMany();
  const assign = useAssignKraSet();
  const unassign = useUnassignKraSet();
  const all = React.useMemo(() => data ?? [], [data]);

  const counts = React.useMemo(
    () => ({
      notStarted: all.filter((r) => r.status === 'NOT_STARTED').length,
      draft: all.filter((r) => r.status === 'DRAFT').length,
      // Live is both statuses: publishing writes ACCEPTED, and the legacy
      // PENDING_ACCEPTANCE rows are live too. Counting only one would under-report.
      live: all.filter((r) => r.status === 'ACCEPTED' || r.status === 'PENDING_ACCEPTANCE')
        .length,
    }),
    [all],
  );

  const rows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((r) => {
      if (status !== ALL && r.status !== status) return false;
      if (!q) return true;
      return [
        r.employeeName,
        r.employeeEmail,
        r.designationTitle,
        r.departmentName,
        r.reportingManagerName,
      ].some((field) => field?.toLowerCase().includes(q) ?? false);
    });
  }, [all, search, status]);

  /**
   * Tickable = on screen AND actionable by at least one of the two buttons. A row
   * neither can touch — somebody else's report, or a live set that is already done —
   * gets no checkbox at all, rather than a tick that is silently dropped on Apply.
   */
  const selectable = React.useMemo(
    () => rows.filter((r) => r.canAssign || r.canUnassign || publishBlocker(r) === null),
    [rows],
  );
  const allTicked = selectable.length > 0 && selectable.every((r) => selected.has(r.employeeId));

  /**
   * What each button will actually send: ticked, still visible under the current
   * filters, and allowed for that action. Ticking someone and then filtering them away
   * must not act on them behind the reader's back — and the count on each button says
   * how many, so it never disagrees with what happens.
   */
  const toAssign = React.useMemo(
    () => selectable.filter((r) => selected.has(r.employeeId) && r.canAssign),
    [selectable, selected],
  );
  const toUnassign = React.useMemo(
    () => selectable.filter((r) => selected.has(r.employeeId) && r.canUnassign),
    [selectable, selected],
  );
  const toPublish = React.useMemo(
    () =>
      selectable
        .filter((r) => selected.has(r.employeeId) && publishBlocker(r) === null)
        .map((r) => r.assignmentId!),
    [selectable, selected],
  );

  function tick(employeeId: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(employeeId);
      else next.delete(employeeId);
      return next;
    });
  }

  async function onAssign(kraSetId: string, kraSetName: string) {
    try {
      const outcomes = await assign.mutateAsync({
        kraSetId,
        employeeIds: toAssign.map((r) => r.employeeId),
      });
      const assigned = outcomes.filter((o) => o.assigned);
      const skipped = outcomes.filter((o) => !o.assigned);

      if (assigned.length > 0) {
        toast.success(`"${kraSetName}" assigned to ${assigned.length} employee(s)`, {
          description:
            skipped.length > 0
              ? `${skipped.length} skipped — see below`
              : 'Review each set, then publish it.',
        });
      }
      for (const skip of skipped) {
        toast.error(`Skipped ${skip.employeeName}`, { description: skip.message ?? undefined });
      }
      setSelected(new Set());
    } catch (e) {
      toast.error('Could not assign that group', { description: err(e) });
    }
  }

  async function onUnassign(reason: string) {
    try {
      const outcomes = await unassign.mutateAsync({
        employeeIds: toUnassign.map((r) => r.employeeId),
        reason,
      });
      const removed = outcomes.filter((o) => o.assigned);
      const skipped = outcomes.filter((o) => !o.assigned);

      if (removed.length > 0) {
        toast.success(`Goals removed from ${removed.length} employee(s)`, {
          description:
            skipped.length > 0
              ? `${skipped.length} skipped — see below`
              : "Recorded in the audit log. Assign the correct group next.",
        });
      }
      for (const skip of skipped) {
        toast.error(`Skipped ${skip.employeeName}`, { description: skip.message ?? undefined });
      }
      setSelected(new Set());
    } catch (e) {
      toast.error("Could not remove those goals", { description: err(e) });
    }
  }

  async function onPublish() {
    try {
      const outcomes = await publishMany.mutateAsync(toPublish);
      const published = outcomes.filter((o) => o.published);
      const skipped = outcomes.filter((o) => !o.published);

      // Per person, never a bare success: the ones it skipped are the reason to look.
      if (published.length > 0) {
        toast.success(`${published.length} goal set(s) published`, {
          description:
            skipped.length > 0
              ? `${skipped.length} skipped — see below`
              : 'Those employees have been told their goals are live.',
        });
      }
      for (const skip of skipped) {
        toast.error(`Skipped ${skip.employeeName}`, { description: skip.message ?? undefined });
      }
      setSelected(new Set());
    } catch (e) {
      toast.error('Could not publish', { description: err(e) });
    }
  }

  return (
    <PageContainer>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatTile
          label="Not started"
          value={isPending ? '—' : counts.notStarted}
          tone={counts.notStarted > 0 ? 'warning' : 'default'}
          caption={
            counts.notStarted === 0
              ? 'everyone has a goal set'
              : 'no goals written or assigned yet'
          }
        />
        <StatTile
          label="Draft"
          value={isPending ? '—' : counts.draft}
          // Amber, unlike a routine in-progress state: an unpublished draft means that
          // employee is not being measured on anything yet.
          tone={counts.draft > 0 ? 'warning' : 'default'}
          caption={counts.draft === 0 ? 'nothing unpublished' : 'written but not published yet'}
        />
        <StatTile
          label="Live"
          value={isPending ? '—' : counts.live}
          caption={`${counts.live} of ${all.length} employees`}
        />
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 py-4 sm:py-4">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            {!search && (
              <span className="pointer-events-none absolute left-8 right-3 top-1/2 -translate-y-1/2 truncate text-sm text-muted-foreground">
                <Typewriter text="Search by name, email, designation, department or manager…" />
              </span>
            )}
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search by name, email, designation, department or manager"
              className="pl-8"
            />
          </div>

          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Combobox
            // Select-only: a group is a weighting that has to total 100%, so one
            // conjured from a name typed here would assign a set nobody could use.
            value={null}
            align="end"
            contentClassName="w-72"
            // Picking a group used to assign it on the spot. A dropdown item is the
            // easiest thing on this screen to hit by accident, and the result was goals
            // created for everyone ticked - so the pick now only proposes it.
            onChange={(id) => {
              const set = (sets ?? []).find((s) => s.id === id);
              if (set) setPendingGroup({ id: set.id, name: set.name });
            }}
            options={(sets ?? []).map((set) => ({
              value: set.id,
              label: set.name,
              hint: `${set.items.length} KRAs · ${set.totalWeightage}%`,
            }))}
            searchPlaceholder="Search groups"
            emptyText="No group matches"
            trigger={
              <Button hint={"Assign this KRA group to the selected people"}
                data-guide="team-assign-group"
                variant="outline"
                className="gap-2"
                disabled={toAssign.length === 0 || assign.isPending}
              >
                <UserCheck className="size-4" />
                {assign.isPending ? 'Assigning…' : `Assign KRA group (${toAssign.length})`}
              </Button>
            }
          />

          {/* Assigning a group to forty people leaves forty drafts; without this they
              are forty pages to open one at a time. It asks first because publishing
              is the moment the goals become binding AND mails every one of them. */}
          <ConfirmButton
            className="gap-2"
            data-guide="team-publish"
            disabled={toPublish.length === 0 || publishMany.isPending}
            title={`Publish ${toPublish.length} goal set${toPublish.length === 1 ? '' : 's'}?`}
            description={`Their goals go live and ${toPublish.length === 1 ? 'that employee is' : 'all of them are'} emailed to say so. From then on the weightage split is what their rating is computed from, and correcting it is a per-employee job.`}
            confirmLabel={`Publish ${toPublish.length}`}
            pending={publishMany.isPending}
            onConfirm={() => void onPublish()}
          >
            <Send className="size-4" />
            {publishMany.isPending ? 'Publishing…' : `Publish selected (${toPublish.length})`}
          </ConfirmButton>

          {/*
            Deliberately unfiltered, and labelled so.

            The search and status filters on this screen run client-side over the whole
            list, and the report route's `search` matches name and email only - so
            passing them through would hand back fewer rows than the screen shows and
            call it the same export. "Export all" means all, which is the one thing the
            server route can say without qualification.
          */}
          {/* Removing somebody's goals needs a reason, and the reason goes in the audit
              trail — that row is what separates an honest correction from a grudge. */}
          <Button hint={"Remove the KRA group from the selected people"}
            variant="outline"
            className="gap-2"
            disabled={toUnassign.length === 0 || unassign.isPending}
            onClick={() => setUnassigning(true)}
          >
            <Undo2 className="size-4" />
            {unassign.isPending ? "Removing…" : `Unassign (${toUnassign.length})`}
          </Button>

          <ServerExportButton path="/reports/goals.csv" filename="goals" />

          {/* The KRA library, reached from the screen that applies it rather than from
              its own sidebar entry. You come looking for it at exactly one moment — the
              group you wanted to assign is not in the picker — and that moment happens
              here. Hidden from individual contributors, the same rule the page itself
              and the API both gate on. */}
          {canManageKra && (
            <Button hint={"Open the shared KRA library"} asChild variant="ghost" size="sm" className="gap-1.5">
              <Link to="/team/kra">
                <Layers className="size-3.5" />
                KRA library
              </Link>
            </Button>
          )}
        </CardContent>
      </Card>

      <UnassignDialog
        open={unassigning}
        onClose={() => setUnassigning(false)}
        count={toUnassign.length}
        reason={unassignReason}
        onReason={setUnassignReason}
        pending={unassign.isPending}
        onConfirm={() => {
          const reason = unassignReason.trim();
          setUnassigning(false);
          setUnassignReason("");
          void onUnassign(reason);
        }}
      />

      {/* Driven from state rather than ConfirmButton: the trigger is a dropdown pick,
          not a button press. */}
      <ConfirmDialog
        open={pendingGroup !== null}
        onOpenChange={(next) => !next && setPendingGroup(null)}
        title={`Assign "${pendingGroup?.name ?? ''}" to ${toAssign.length} employee${toAssign.length === 1 ? '' : 's'}?`}
        description={`Each of them gets that group's KRAs as their organization goals, as a draft nobody is measured on until it is published. Anyone who already has goals is skipped rather than overwritten, and you will be told who.`}
        confirmLabel={`Assign to ${toAssign.length}`}
        pending={assign.isPending}
        onConfirm={() => {
          const group = pendingGroup;
          setPendingGroup(null);
          if (group) void onAssign(group.id, group.name);
        }}
      />

      <Card>
        <CardContent className="p-0 sm:p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      aria-label="Select everyone actionable in this list"
                      data-guide="team-select-all"
                      checked={allTicked}
                      disabled={selectable.length === 0}
                      onCheckedChange={(on) =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          for (const row of selectable) {
                            if (on) next.add(row.employeeId);
                            else next.delete(row.employeeId);
                          }
                          return next;
                        })
                      }
                    />
                  </TableHead>
                  <TableHead className="min-w-[220px]">Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Reports to</TableHead>
                  <TableHead>Organization goals</TableHead>
                  <TableHead>From group</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>
                    {cycle?.currentQuarterIndex
                      ? `Q${cycle.currentQuarterIndex} self assessment`
                      : 'Self assessment'}
                  </TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={10}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))}

                {error && (
                  <TableRow>
                    <TableCell colSpan={10} className="py-10 text-center text-sm text-destructive">
                      {error.message}
                    </TableCell>
                  </TableRow>
                )}

                {!isPending && !error && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="py-14 text-center">
                      <Target className="mx-auto mb-3 size-6 text-muted-foreground" />
                      <p className="text-sm font-medium">
                        {all.length === 0
                          ? 'Nobody to set goals for'
                          : 'No employees match these filters'}
                      </p>
                      {all.length === 0 && (
                        <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                          You set goals for your own direct reports. HR sets them for anyone —
                          reassigning a reporting line is theirs to do.
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((row) => (
                  <EmployeeRow
                    key={row.employeeId}
                    row={row}
                    ticked={selected.has(row.employeeId)}
                    onToggle={(on) => tick(row.employeeId, on)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}

function EmployeeRow({
  row,
  ticked,
  onToggle,
}: {
  row: GoalSetSummary;
  ticked: boolean;
  onToggle: (on: boolean) => void;
}) {
  const cannotPublish = publishBlocker(row);
  // One reason, and the useful one: if they can be assigned to, say nothing; if they
  // cannot, the assign blocker is why (it covers "not your report" too). Otherwise it
  // is the publish blocker, which is what is left.
  const blocker = row.canAssign ? null : (row.blockedMessage ?? cannotPublish);
  // Same three as `selectable`: leaving canUnassign out hid Unassign on exactly the rows
  // that need it - a draft too short to publish and too full to assign over.
  const tickable = row.canAssign || row.canUnassign || cannotPublish === null;

  return (
    <TableRow className={ticked ? 'bg-accent/50' : undefined}>
      <TableCell>
        {tickable ? (
          <Checkbox
            aria-label={`Select ${row.employeeName}`}
            checked={ticked}
            onCheckedChange={(on) => onToggle(on === true)}
          />
        ) : (
          // Blocked rows cannot be ticked at all, rather than ticked and then silently
          // skipped on Apply. This is the guarantee the screen rests on: a bulk action
          // must never quietly overwrite goals somebody spent an afternoon writing.
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex size-4 items-center justify-center">
                <Info className="size-3.5 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-64">{blocker}</TooltipContent>
          </Tooltip>
        )}
      </TableCell>

      <TableCell>
        <div className="font-medium">{row.employeeName}</div>
        <div className="text-xs text-muted-foreground">{row.employeeEmail}</div>
      </TableCell>

      <TableCell className="text-sm">
        {row.departmentName ?? <span className="text-warning">Not set</span>}
        {row.parentDepartmentName && (
          <span className="block text-xs text-muted-foreground">
            under {row.parentDepartmentName}
          </span>
        )}
      </TableCell>

      {/* A KRA group is pinned to job titles, so this is the column that says which
          group applies to this person. */}
      <TableCell className="text-sm">
        {row.designationTitle ?? <span className="text-muted-foreground">—</span>}
      </TableCell>

      <TableCell className="text-sm">
        {/* No manager means nobody can assign organization goals at all, so it is the
            cause of a NOT_STARTED row more often than not. */}
        {row.reportingManagerName ?? <span className="text-warning">Not set</span>}
      </TableCell>

      <TableCell className="text-sm">
        {row.orgGoalCount === 0 ? (
          <span className="text-muted-foreground">None</span>
        ) : (
          <>
            {row.orgGoalCount} goals
            <span
              className={
                row.orgTotalWeightage === 100
                  ? 'ml-2 text-xs text-muted-foreground'
                  : 'ml-2 text-xs text-warning'
              }
            >
              {row.orgTotalWeightage}%
            </span>
          </>
        )}
        {row.individualCount > 0 && (
          <span className="block text-xs text-muted-foreground">
            +{row.individualCount} development
          </span>
        )}
      </TableCell>

      <TableCell className="text-sm">
        {row.assignedFromName ?? <span className="text-muted-foreground">—</span>}
      </TableCell>

      <TableCell>
        <Badge variant={goalAssignmentVariant(row.status)} className={PILL_CLASS}>
          {STATUS_LABELS[row.status]}
        </Badge>
      </TableCell>

      <TableCell>
        <Badge variant={assessmentStatusVariant(row.assessmentStatus)} className={PILL_CLASS}>
          {ASSESSMENT_LABELS[row.assessmentStatus]}
        </Badge>
      </TableCell>

      <TableCell>
        <Button hint={"Open this person's goals"} asChild variant="ghost" size="sm">
          <Link to={`/team/${row.employeeId}/goals`}>Open</Link>
        </Button>
      </TableCell>
    </TableRow>
  );
}

/**
 * Removing somebody's goals, with the reason that goes in the audit trail.
 *
 * A reason field rather than a plain confirm, and required. This is the only action in
 * the product that takes work away from somebody, so the audit row has to answer "who
 * did this to me and what did they say" months later — without it an honest correction
 * and a deliberate one read exactly the same.
 *
 * Their own development goals are untouched, and the dialog says so: they were never
 * part of what was assigned, and losing them would punish the employee for somebody
 * else's mis-click.
 */
function UnassignDialog({
  open,
  onClose,
  count,
  reason,
  onReason,
  onConfirm,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  count: number;
  reason: string;
  onReason: (v: string) => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Remove the goals from {count} employee{count === 1 ? '' : 's'}?
          </DialogTitle>
          <DialogDescription>
            Their organization goals are deleted and the set goes back to a draft, ready for
            the right group. Their own development goals are untouched. Anyone already rated
            against these goals is skipped — removing those would delete the ratings too.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={3}
          value={reason}
          onChange={(e) => onReason(e.target.value)}
          maxLength={1000}
          aria-label="Why these goals are being removed"
          placeholder="Why are these being removed? This is recorded in the audit log."
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button hint={"Remove the KRA group from these people"}
            variant="destructive"
            disabled={reason.trim().length === 0 || pending}
            onClick={onConfirm}
          >
            {pending ? 'Removing…' : `Remove from ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
